import { useState } from 'react';
import styled from 'styled-components';
import {
  useGetGeoRdDocumentsQuery,
  useRefreshGeoSheetRdMutation,
  useTakeGeoSheetFromRdMutation,
} from '@shared/api';
import type { GeoRdDocument, GeoScheme } from '@entities/geodesy';
import { Button } from '@shared/ui/Button';
import { Select } from '@shared/ui/Input';
import {
  ErrorText,
  FieldGrid,
  FieldLabel,
  Hint,
  Notice,
  Stack,
  useAction,
} from '@widgets/GeoSchemes';
import { useWizard } from './wizard';

/** Статусы, в которых исходные данные ИС ещё меняются (как на сервере). */
const EDITABLE = new Set(['draft', 'generated', 'rejected']);

const Title = styled.h4`
  margin: 0;
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

/** «Изм.2» (метка редакции из реестра) или «ред. 2». */
export function revisionName(label: string | null, no: number): string {
  return label?.trim() ? label.trim() : `ред. ${no}`;
}

function docLabel(d: GeoRdDocument): string {
  const mark = d.mark ? `${d.mark} — ` : '';
  return `${mark}${d.code} «${d.title}» · ${revisionName(d.rev_label, d.revision_no)}`;
}

/**
 * Лист ИС из реестра РД ПТО (шаг 1): документы объекта с действующей
 * редакцией — выбрать документ и файл листа. Лист копируется в ИС, ссылка на
 * редакцию остаётся: выйдет изменение — карточка предупредит. ИС без объекта —
 * подсказка выбрать «Проект ПТО».
 */
export function RdSheetPicker() {
  const { scheme, readOnly, goTo } = useWizard();
  const skip = readOnly || !scheme.project_id;
  const { data, isFetching } = useGetGeoRdDocumentsQuery(scheme.id, { skip });
  const [take] = useTakeGeoSheetFromRdMutation();
  const taking = useAction();
  const [docId, setDocId] = useState('');
  const [file, setFile] = useState('');

  if (readOnly) return null;
  if (!scheme.project_id) {
    return (
      <Hint>
        Лист можно взять прямо из реестра РД ПТО — выберите «Проект ПТО» в реквизитах ИС{' '}
        <Button type="button" $variant="ghost" $size="sm" onClick={() => goTo(4)}>
          Реквизиты — шаг 4 →
        </Button>
      </Hint>
    );
  }
  const docs = data?.documents ?? [];
  if (!data) {
    return <Hint>{isFetching ? 'Смотрим реестр РД объекта…' : 'Реестр РД не загрузился.'}</Hint>;
  }
  if (docs.length === 0) {
    return (
      <Hint>
        В реестре РД объекта «{data.project?.name}» нет документов с листами PDF или DXF — загрузите
        лист с диска.
      </Hint>
    );
  }
  const doc = docs.find((d) => d.document_id === docId) ?? null;
  const files = doc?.files ?? [];
  const chosen = files.length === 1 ? files[0] : file;

  const run = () => {
    if (!doc || !chosen) return;
    if (
      scheme.sheet &&
      !window.confirm(
        'Лист заменится выбранным из реестра РД: привязку и проектные элементы проверьте заново. Продолжить?',
      )
    )
      return;
    void taking.run(
      () => take({ id: scheme.id, revision_id: doc.revision_id, filename: chosen }).unwrap(),
      'Лист из реестра РД не взят.',
    );
  };

  return (
    <Stack $gap={3}>
      <Title>Из реестра РД ПТО</Title>
      <Hint>
        Действующая редакция документа объекта «{data.project?.name}». Выйдет изменение листа в ПТО
        — карточка ИС предупредит, выносить по старой редакции не придётся.
      </Hint>
      <FieldGrid $min={240}>
        <FieldLabel>
          Документ
          <Select
            value={docId}
            onChange={(e) => {
              setDocId(e.target.value);
              setFile('');
            }}
          >
            <option value="">— выберите —</option>
            {docs.map((d) => (
              <option key={d.document_id} value={d.document_id}>
                {docLabel(d)}
              </option>
            ))}
          </Select>
        </FieldLabel>
        {files.length > 1 && (
          <FieldLabel>
            Файл листа
            <Select value={file} onChange={(e) => setFile(e.target.value)}>
              <option value="">— выберите —</option>
              {files.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </Select>
          </FieldLabel>
        )}
      </FieldGrid>
      <div>
        <Button
          type="button"
          $variant="secondary"
          $size="sm"
          onClick={run}
          disabled={taking.busy || !doc || !chosen}
        >
          {taking.busy ? 'Берём лист…' : 'Взять лист из реестра'}
        </Button>
      </div>
      {taking.error && <ErrorText role="alert">{taking.error}</ErrorText>}
    </Stack>
  );
}

/**
 * Предупреждение карточки ИС: в реестре РД ПТО вышла новая редакция листа, по
 * которому строится схема, — взять её одной кнопкой.
 */
export function SheetRdUpdateNotice({ scheme }: { scheme: GeoScheme }) {
  const { readOnly } = useWizard();
  const [refresh] = useRefreshGeoSheetRdMutation();
  const refreshing = useAction();
  const rd = scheme.sheet?.rd;
  const upd = scheme.sheet_rd_update;
  if (!rd || !upd) return null;
  const canEdit = !readOnly && EDITABLE.has(scheme.status);
  const date = new Date(upd.uploaded_at).toLocaleDateString('ru-RU');

  const run = () => {
    if (
      !window.confirm(
        'Лист заменится новой редакцией из реестра РД: привязку и проектные элементы проверьте заново. Продолжить?',
      )
    )
      return;
    void refreshing.run(() => refresh(scheme.id).unwrap(), 'Новая редакция не взята.');
  };

  return (
    <Notice $tone="warning" role="status">
      <strong>
        Лист РД устарел: в реестре ПТО вышла {revisionName(upd.rev_label, upd.revision_no)}{' '}
        документа {rd.code} ({date})
      </strong>
      <span>
        Схема строится по {revisionName(rd.rev_label, rd.revision_no)} — по ней выносить и сличать
        нельзя.
      </span>
      {canEdit && (
        <div>
          <Button
            type="button"
            $variant="secondary"
            $size="sm"
            onClick={run}
            disabled={refreshing.busy}
          >
            {refreshing.busy ? 'Берём новую редакцию…' : 'Взять новую редакцию'}
          </Button>
        </div>
      )}
      {refreshing.error && <ErrorText role="alert">{refreshing.error}</ErrorText>}
    </Notice>
  );
}
